/**
 * Date range helpers for report comparison presets.
 * Uses the browser local calendar (same as `<input type="date">`).
 *
 * Week preset: local Monday–Sunday weeks. "Previous week" = last completed
 * ISO-style week strictly before the Monday of the week containing today.
 */

export type DateRange = { startDate: string; endDate: string };

export function toIsoDate(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function startOfLocalDay(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

/** Monday of the local week containing `d` (week starts Monday). */
export function getLocalMonday(d: Date): Date {
  const x = startOfLocalDay(d);
  const dow = x.getDay();
  const delta = dow === 0 ? -6 : 1 - dow;
  x.setDate(x.getDate() + delta);
  return x;
}

/** Last full Mon–Sun week that ended before the current (possibly partial) week. */
export function getLastCompletedLocalMonSunWeek(now = new Date()): DateRange {
  const today = startOfLocalDay(now);
  const mondayThisWeek = getLocalMonday(today);
  const sundayLast = new Date(mondayThisWeek);
  sundayLast.setDate(sundayLast.getDate() - 1);
  const mondayLast = new Date(sundayLast);
  mondayLast.setDate(mondayLast.getDate() - 6);
  return { startDate: toIsoDate(mondayLast), endDate: toIsoDate(sundayLast) };
}

/** The Mon–Sun week immediately before `week` (same length). */
export function getPriorMonSunWeek(week: DateRange): DateRange {
  const start = new Date(week.startDate + 'T12:00:00');
  const prevSunday = new Date(start);
  prevSunday.setDate(prevSunday.getDate() - 1);
  const prevMonday = new Date(prevSunday);
  prevMonday.setDate(prevMonday.getDate() - 6);
  return { startDate: toIsoDate(prevMonday), endDate: toIsoDate(prevSunday) };
}

/**
 * Previous calendar month (M-1) vs the month before that (M-2), local time.
 * Period A = M-1 (more recent), period B = M-2 (baseline for % change).
 */
export function getPreviousMonthVsPriorMonth(now = new Date()): { periodA: DateRange; periodB: DateRange } {
  const y = now.getFullYear();
  const m = now.getMonth();
  const aStart = new Date(y, m - 1, 1);
  const aEnd = new Date(y, m, 0);
  const bStart = new Date(y, m - 2, 1);
  const bEnd = new Date(y, m - 1, 0);
  return {
    periodA: { startDate: toIsoDate(aStart), endDate: toIsoDate(aEnd) },
    periodB: { startDate: toIsoDate(bStart), endDate: toIsoDate(bEnd) },
  };
}

/** Previous calendar month as YYYY-MM (for admin account metrics default). */
export function getDefaultMetricsMonth(now = new Date()): string {
  const { periodA } = getPreviousMonthVsPriorMonth(now);
  return periodA.startDate.slice(0, 7);
}

/** Human-readable label for a YYYY-MM month value. */
export function formatMonthLabel(month: string): string {
  const [yearStr, monthStr] = month.split('-');
  const year = Number(yearStr);
  const monthIndex = Number(monthStr) - 1;
  if (!Number.isFinite(year) || !Number.isFinite(monthIndex) || monthIndex < 0 || monthIndex > 11) {
    return month;
  }
  return new Intl.DateTimeFormat('en-US', { month: 'long', year: 'numeric' }).format(
    new Date(year, monthIndex, 1)
  );
}

export function getPreviousWeekVsPriorWeek(now = new Date()): { periodA: DateRange; periodB: DateRange } {
  const periodA = getLastCompletedLocalMonSunWeek(now);
  const periodB = getPriorMonSunWeek(periodA);
  return { periodA, periodB };
}

export function isRangeValid(r: DateRange): boolean {
  return Boolean(r.startDate && r.endDate && r.endDate >= r.startDate);
}

/** Dashboard default: today through 7 days ago (local calendar). */
export function getDefaultDashboardDateRange(now = new Date()): DateRange {
  const today = startOfLocalDay(now);
  const start = new Date(today);
  start.setDate(start.getDate() - 7);
  return { startDate: toIsoDate(start), endDate: toIsoDate(today) };
}

/** Today vs yesterday (local calendar). */
export function getTodayVsYesterday(now = new Date()): { current: DateRange; previous: DateRange } {
  const today = startOfLocalDay(now);
  const t = toIsoDate(today);
  const current = { startDate: t, endDate: t };
  return { current, previous: getPriorPeriod(current) };
}

/** Inclusive range ending today. `days` of 1 is today only. */
export function getInclusiveRangeEndingToday(days: number, now = new Date()): DateRange {
  const end = startOfLocalDay(now);
  const start = new Date(end);
  start.setDate(start.getDate() - (days - 1));
  return { startDate: toIsoDate(start), endDate: toIsoDate(end) };
}

export const SOV_DATE_PRESETS: { id: string; label: string }[] = [
  { id: 'today', label: 'Today' },
  { id: 'yesterday', label: 'Yesterday' },
  { id: 'last7', label: 'Last 7 days' },
  { id: 'last30', label: 'Last 30 days' },
];

/** Share of voice presets include today. Campaign presets end yesterday because reports lag. */
export function resolveSovDatePreset(id: string, now = new Date()): DateRange {
  switch (id) {
    case 'today':
      return getInclusiveRangeEndingToday(1, now);
    case 'yesterday':
      return getYesterdayVsPriorDay(now).current;
    case 'last7':
      return getInclusiveRangeEndingToday(7, now);
    case 'last30':
      return getInclusiveRangeEndingToday(30, now);
    default:
      return getInclusiveRangeEndingToday(1, now);
  }
}

/** Yesterday vs the day before (local calendar). */
export function getYesterdayVsPriorDay(now = new Date()): { current: DateRange; previous: DateRange } {
  const today = startOfLocalDay(now);
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);
  const prior = new Date(yesterday);
  prior.setDate(prior.getDate() - 1);
  const y = toIsoDate(yesterday);
  const p = toIsoDate(prior);
  return {
    current: { startDate: y, endDate: y },
    previous: { startDate: p, endDate: p },
  };
}

/**
 * This month so far (1st → today) vs the same dates last month.
 * If today is a 31st and the prior month is shorter, the compare end is clamped
 * to that month's last day.
 */
export function getThisMonthVsPriorMonthToDate(
  now = new Date()
): { current: DateRange; previous: DateRange } {
  const today = startOfLocalDay(now);
  const currentStart = new Date(today.getFullYear(), today.getMonth(), 1);
  const previousStart = new Date(today.getFullYear(), today.getMonth() - 1, 1);
  const lastDayOfPreviousMonth = new Date(today.getFullYear(), today.getMonth(), 0).getDate();
  const previousEndDay = Math.min(today.getDate(), lastDayOfPreviousMonth);
  const previousEnd = new Date(today.getFullYear(), today.getMonth() - 1, previousEndDay);
  return {
    current: { startDate: toIsoDate(currentStart), endDate: toIsoDate(today) },
    previous: { startDate: toIsoDate(previousStart), endDate: toIsoDate(previousEnd) },
  };
}

function getLastNDaysEndingYesterday(days: number, now = new Date()): DateRange {
  const today = startOfLocalDay(now);
  const end = new Date(today);
  end.setDate(end.getDate() - 1);
  const start = new Date(end);
  start.setDate(start.getDate() - (days - 1));
  return { startDate: toIsoDate(start), endDate: toIsoDate(end) };
}

/** Equal-length prior period immediately before `range`. */
export function getPriorPeriod(range: DateRange): DateRange {
  const start = new Date(range.startDate + 'T12:00:00');
  const end = new Date(range.endDate + 'T12:00:00');
  const spanDays = Math.round((end.getTime() - start.getTime()) / 86400000) + 1;
  const compareEnd = new Date(start);
  compareEnd.setDate(compareEnd.getDate() - 1);
  const compareStart = new Date(compareEnd);
  compareStart.setDate(compareStart.getDate() - (spanDays - 1));
  return { startDate: toIsoDate(compareStart), endDate: toIsoDate(compareEnd) };
}

export function getLast7DaysVsPrior7(now = new Date()): { current: DateRange; previous: DateRange } {
  const current = getLastNDaysEndingYesterday(7, now);
  return { current, previous: getPriorPeriod(current) };
}

export function getLast14DaysVsPrior14(now = new Date()): { current: DateRange; previous: DateRange } {
  const current = getLastNDaysEndingYesterday(14, now);
  return { current, previous: getPriorPeriod(current) };
}

export function getLast30DaysVsPrior30(now = new Date()): { current: DateRange; previous: DateRange } {
  const current = getLastNDaysEndingYesterday(30, now);
  return { current, previous: getPriorPeriod(current) };
}

export function getLast60DaysVsPrior60(now = new Date()): { current: DateRange; previous: DateRange } {
  const current = getLastNDaysEndingYesterday(60, now);
  return { current, previous: getPriorPeriod(current) };
}

export type WorkspaceDatePresetId =
  | 'today'
  | 'yesterday'
  | 'last7'
  | 'last14'
  | 'last30'
  | 'last60'
  | 'lastWeek'
  | 'thisMonth'
  | 'lastMonth';

export const WORKSPACE_DATE_PRESETS: { id: WorkspaceDatePresetId; label: string }[] = [
  { id: 'today', label: 'Today' },
  { id: 'yesterday', label: 'Yesterday' },
  { id: 'last7', label: 'Last 7 days' },
  { id: 'last14', label: 'Last 14 days' },
  { id: 'last30', label: 'Last 30 days' },
  { id: 'last60', label: 'Last 60 days' },
  { id: 'lastWeek', label: 'Last week' },
  { id: 'thisMonth', label: 'This month' },
  { id: 'lastMonth', label: 'Last month' },
];

export function dateRangesEqual(a: DateRange, b: DateRange): boolean {
  return a.startDate === b.startDate && a.endDate === b.endDate;
}

export function resolveWorkspaceDatePreset(
  id: WorkspaceDatePresetId,
  now = new Date()
): { current: DateRange; previous: DateRange } {
  switch (id) {
    case 'today':
      return getTodayVsYesterday(now);
    case 'yesterday':
      return getYesterdayVsPriorDay(now);
    case 'last7':
      return getLast7DaysVsPrior7(now);
    case 'last14':
      return getLast14DaysVsPrior14(now);
    case 'last30':
      return getLast30DaysVsPrior30(now);
    case 'last60':
      return getLast60DaysVsPrior60(now);
    case 'lastWeek': {
      const { periodA, periodB } = getPreviousWeekVsPriorWeek(now);
      return { current: periodA, previous: periodB };
    }
    case 'thisMonth':
      return getThisMonthVsPriorMonthToDate(now);
    case 'lastMonth': {
      const { periodA, periodB } = getPreviousMonthVsPriorMonth(now);
      return { current: periodA, previous: periodB };
    }
    default:
      return getYesterdayVsPriorDay(now);
  }
}

export function matchesWorkspaceDatePreset(
  id: WorkspaceDatePresetId,
  current: DateRange,
  previous: DateRange,
  now = new Date()
): boolean {
  const preset = resolveWorkspaceDatePreset(id, now);
  return dateRangesEqual(current, preset.current) && dateRangesEqual(previous, preset.previous);
}

export type CampaignScheduleDates = { startDate?: string; endDate?: string };

const MIN_ISO_DATE = '1970-01-01';
const MAX_ISO_DATE = '9999-12-31';

function parseIsoDateOnly(value: string | undefined): string | null {
  if (value === undefined || value === null) return null;
  const s = String(value).trim();
  if (!s) return null;
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
  if (/^\d{8}$/.test(s)) {
    return `${s.slice(0, 4)}-${s.slice(4, 6)}-${s.slice(6, 8)}`;
  }
  return null;
}

function resolveCampaignDateBound(
  value: string | undefined,
  missingFallback: string
): string | null {
  if (value === undefined || value === null || value === '') return missingFallback;
  return parseIsoDateOnly(value);
}

/**
 * True when the campaign schedule interval overlaps the selected range.
 * Missing start/end are treated as unbounded; unparsable provided dates exclude the campaign.
 */
export function campaignScheduleOverlapsRange(
  campaign: CampaignScheduleDates,
  range: DateRange
): boolean {
  if (!isRangeValid(range)) return true;

  const rangeStart = parseIsoDateOnly(range.startDate);
  const rangeEnd = parseIsoDateOnly(range.endDate);
  if (!rangeStart || !rangeEnd) return false;

  const campaignStart = resolveCampaignDateBound(campaign.startDate, MIN_ISO_DATE);
  if (campaignStart === null) return false;
  const campaignEnd = resolveCampaignDateBound(campaign.endDate, MAX_ISO_DATE);
  if (campaignEnd === null) return false;

  return campaignStart <= rangeEnd && campaignEnd >= rangeStart;
}
